import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { billTotals } from "../utils/billTotals";
import { fmt, today } from "../utils";

const emptyLine = { item_id: "", batch_id: "", qty: "", rate: "", discount_pct: 0 };

export default function SalesForm() {
  const [hospitals, setHospitals] = useState([]);
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [hospitalId, setHospitalId] = useState("");
  const [orderNo, setOrderNo] = useState("");
  const [doctorRef, setDoctorRef] = useState("");
  const [transport, setTransport] = useState("");
  const [vehicleNo, setVehicleNo] = useState("");
  const [ewayNo, setEwayNo] = useState("");
  const [lrNo, setLrNo] = useState("");
  const [quotationId, setQuotationId] = useState(null);
  const [date, setDate] = useState(today());
  const [lines, setLines] = useState([{ ...emptyLine }]);
  const submitting = useRef(false);
  const requestKey = useRef(null);
  const [saving, setSaving] = useState(false);
  const [originalDue, setOriginalDue] = useState(0);
  const [originalPaid, setOriginalPaid] = useState(0);
  const nav = useNavigate();
  const { id: editId } = useParams();
  const [params] = useSearchParams();

  useEffect(() => {
    Promise.all([
      api.get("/hospitals"),
      api.get("/items"),
      api.get("/batches"),
      editId ? api.get(`/sales/${editId}`) : Promise.resolve(null),
    ]).then(([h, i, b, saleRes]) => {
      setHospitals(h.data);
      setItems(i.data);
      setBatches(b.data);
      if (saleRes?.data) {
        const sale = saleRes.data;
        setHospitalId(sale.hospital_id || "");
        setDate(sale.date || today());
        setOrderNo(sale.order_no || "");
        setDoctorRef(sale.doctor_ref || "");
        setTransport(sale.transport || "");
        setVehicleNo(sale.vehicle_no || "");
        setEwayNo(sale.eway_no || "");
        setLrNo(sale.lr_no || "");
        setQuotationId(sale.quotation_id || null);
        setOriginalPaid(Number(sale.paid_amount) || 0);
        setOriginalDue(Math.max(0, (Number(sale.grand_total) || 0) - (Number(sale.paid_amount) || 0)));
        setLines((sale.items || []).map((line) => ({
          item_id: line.item_id || "", batch_id: line.batch_id || "", qty: line.qty ?? "",
          rate: line.rate ?? "", discount_pct: line.discount_pct || 0,
          original_batch_id: line.batch_id || "", original_qty: Number(line.qty) || 0,
        })));
      }
    }).catch(() => toast.error("Sales Bill data load nahi ho paaya"));
  }, [editId]);

  useEffect(() => {
    const qid = params.get("from_quotation");
    if (!qid || editId) return;
    Promise.all([api.get(`/quotations/${qid}`), api.get("/batches"), api.get("/items")]).then(([quoteRes, batchRes, itemRes]) => {
      const q = quoteRes.data;
      const liveBatches = batchRes.data || [];
      const liveItems = itemRes.data || [];
      setBatches(liveBatches);
      setItems(liveItems);
      setQuotationId(q.id);
      setHospitalId(q.hospital_id);
      setOrderNo(q.order_no || "");
      let mapped = 0;
      setLines(q.items.map((it) => {
        const exactMaster = (it.item_id && liveItems.find((x) => x.id === it.item_id))
          || liveItems.find((x) => x.name.trim().toLowerCase() === String(it.item_name || "").trim().toLowerCase());
        const itemId = exactMaster?.id || "";
        const candidates = liveBatches.filter((b) => b.item_id === itemId && b.qty > 0 && !b.expired);
        const enough = candidates.find((b) => Number(b.qty || 0) + 0.000001 >= Number(it.qty || 0));
        if (itemId && enough) mapped += 1;
        return {
          item_id: itemId,
          batch_id: enough?.id || "",
          qty: it.qty,
          rate: it.rate,
          discount_pct: it.discount_pct,
          quoted_name: it.item_name || "",
        };
      }));
      if (mapped === q.items.length && q.items.length) {
        toast.success(`Quotation ${q.quote_no} loaded — all rows stock master + FEFO batch se auto-mapped`);
      } else {
        toast.info(`Quotation ${q.quote_no} loaded — ${mapped}/${q.items.length} rows auto-mapped; remaining rows manually stock item/batch se map karo`);
      }
    }).catch((err) => toast.error(err.response?.data?.detail || err.message || "Quotation load nahi ho paaya"));
  }, [params, editId]);

  const hospital = hospitals.find((h) => h.id === hospitalId);
  const interstate = (hospital?.gstin?.slice(0, 2) || hospital?.state_code || "10") !== "10";
  const [fin, setFin] = useState(null);
  useEffect(() => {
    if (hospitalId) api.get(`/hospitals/${hospitalId}/outstanding`).then((r) => setFin(r.data)).catch(() => {});
    else setFin(null);
  }, [hospitalId]);
  const setLine = (i, patch) => {
    const next = [...lines];
    next[i] = { ...next[i], ...patch };
    setLines(next);
  };
  const batchesFor = (itemId, selectedBatchId) => batches.filter((b) => b.item_id === itemId && ((b.qty > 0 && !b.expired) || b.id === selectedBatchId));

  const totals = useMemo(() => billTotals(lines, items, interstate), [lines, items, interstate]);

  const submit = async (e) => {
    e.preventDefault();
    if (submitting.current) return;
    const started = lines.filter((l) => l.item_id || l.batch_id || l.qty !== "" || l.rate !== "" || l.quoted_name);
    const valid = started.filter((l) => l.item_id && l.batch_id && Number(l.qty) > 0 && Number(l.rate) > 0);
    if (!hospitalId) return toast.error("Hospital select karo");
    if (!valid.length) return toast.error("Har line mein item, batch, quantity aur rate chahiye");
    if (valid.length !== started.length) return toast.error("Kisi sales row me stock item/batch/quantity/rate incomplete hai — row map, complete ya delete karo");
    const requestedByBatch = {};
    const originalByBatch = {};
    valid.forEach((l) => {
      requestedByBatch[l.batch_id] = (requestedByBatch[l.batch_id] || 0) + Number(l.qty || 0);
      if (editId && l.original_batch_id) {
        originalByBatch[l.original_batch_id] = (originalByBatch[l.original_batch_id] || 0) + Number(l.original_qty || 0);
      }
    });
    const shortageBatchId = Object.keys(requestedByBatch).find((batchId) => {
      const batch = batches.find((b) => b.id === batchId);
      const available = Number(batch?.qty || 0) + Number(originalByBatch[batchId] || 0);
      return requestedByBatch[batchId] > available + 0.000001;
    });
    if (shortageBatchId) {
      const batch = batches.find((b) => b.id === shortageBatchId);
      const available = Number(batch?.qty || 0) + Number(originalByBatch[shortageBatchId] || 0);
      return toast.error(`Batch ${batch?.batch_no || shortageBatchId} me total ${available} available hai, rows me ${requestedByBatch[shortageBatchId]} requested hai`);
    }
    const projectedOutstanding = editId
      ? (fin?.outstanding || 0) - originalDue + Math.max(0, totals.grand - originalPaid)
      : (fin?.outstanding || 0) + totals.grand;
    if (originalPaid > totals.grand)
      return toast.error("Edited bill total already received amount se kam nahi ho sakta");
    if (fin?.credit_limit > 0 && projectedOutstanding > fin.credit_limit)
      return toast.error(`Credit limit (${fmt(fin.credit_limit)}) cross ho jayegi — pehle payment recover karo ya Hospital Master mein limit badhao`);
    submitting.current = true;
    setSaving(true);
    try {
      const payload = {
        hospital_id: hospitalId, date, order_no: orderNo, quotation_id: quotationId,
        doctor_ref: doctorRef, transport, vehicle_no: vehicleNo, eway_no: ewayNo, lr_no: lrNo,
        items: valid.map((l) => ({
          item_id: l.item_id, batch_id: l.batch_id, qty: Number(l.qty),
          rate: Number(l.rate), discount_pct: Number(l.discount_pct) || 0,
        })),
      };
      const fingerprint = JSON.stringify(payload);
      if (requestKey.current?.fingerprint !== fingerprint) requestKey.current = { fingerprint, key: crypto.randomUUID() };
      const bill = (await (editId ? api.put(`/sales/${editId}`, payload) : api.post("/sales", payload, { headers: { "Idempotency-Key": requestKey.current.key } }))).data;
      toast.success(editId ? `Invoice ${bill.bill_no} updated — audit trail saved` : `Invoice ${bill.bill_no} saved — stock updated`);
      nav(`/print/sale/${bill.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Sales Bill save nahi ho paaya");
    } finally {
      submitting.current = false;
      setSaving(false);
    }
  };

  return (
    <form data-testid="sales-form" onSubmit={submit} className="sgp-page max-w-6xl">
      <div className="sgp-page-header">
        <div>
          <h1 className="sgp-page-title">{editId ? "Edit Sales Bill (Tax Invoice)" : "New Sales Bill (Tax Invoice)"}</h1>
          <p className="sgp-page-subtitle">{quotationId ? "Quotation reference loaded — this is still a separate Tax Invoice. Stock changes only when this Sales Bill is saved." : "Tax Invoice uses actual stock batches; saving deducts stock and creates the receivable."}</p>
        </div>
        <button type="submit" data-testid="sale-save-btn" disabled={saving}
          className="sgp-primary-btn px-5 disabled:opacity-50 disabled:pointer-events-none">
          {saving ? "Saving..." : editId ? "Save Changes & Print" : "Save & Print"}
        </button>
      </div>

      <div className="sgp-card p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="sgp-label">Hospital / Institution *</label>
          <select data-testid="sale-hospital-select" required className="sgp-input" value={hospitalId} onChange={(e) => setHospitalId(e.target.value)}>
            <option value="">— Select from master —</option>
            {hospitals.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </div>
        <div>
          <label className="sgp-label">PO / Order No.</label>
          <input data-testid="sale-order-no" className="sgp-input font-mono" value={orderNo} onChange={(e) => setOrderNo(e.target.value)} />
        </div>
        <div>
          <label className="sgp-label">Date</label>
          <input data-testid="sale-date" type="date" className="sgp-input font-mono" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        {hospital && (
          <p className="sm:col-span-3 text-xs text-slate-500">
            GSTIN: <span className="font-mono">{hospital.gstin || "Unregistered"}</span> ·{" "}
            <span className={interstate ? "text-indigo-600 font-semibold" : "text-teal-700 font-semibold"}>{interstate ? "IGST (Interstate)" : "CGST + SGST (Bihar, Place of Supply: 10-Bihar)"}</span>
          </p>
        )}
        {hospital && fin && (
          <div data-testid="credit-limit-banner" className={`sm:col-span-3 text-xs px-3 py-2 rounded-md border ${fin.credit_limit > 0 && (editId ? fin.outstanding - originalDue + Math.max(0, totals.grand - originalPaid) : fin.outstanding + totals.grand) > fin.credit_limit ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
            Outstanding: <b className="font-mono">{fmt(fin.outstanding)}</b>
            {fin.credit_limit > 0 && <> · Credit Limit: <b className="font-mono">{fmt(fin.credit_limit)}</b>
              {(editId ? fin.outstanding - originalDue + Math.max(0, totals.grand - originalPaid) : fin.outstanding + totals.grand) > fin.credit_limit && <b> — Is bill ke saath limit CROSS ho jayegi!</b>}</>}
            {fin.credit_limit <= 0 && <span className="text-slate-500"> · Koi credit limit set nahi (Hospital Master mein set karo)</span>}
          </div>
        )}
      </div>

      <div className="sgp-card p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Transport & Reference (optional — invoice pe dikhega)</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <div><label className="sgp-label">Doctor Ref.</label>
            <input data-testid="sale-doctor-ref" className="sgp-input" value={doctorRef} onChange={(e) => setDoctorRef(e.target.value)} /></div>
          <div><label className="sgp-label">Transport</label>
            <input data-testid="sale-transport" className="sgp-input" value={transport} onChange={(e) => setTransport(e.target.value)} /></div>
          <div><label className="sgp-label">Vehicle No.</label>
            <input data-testid="sale-vehicle" className="sgp-input font-mono" value={vehicleNo} onChange={(e) => setVehicleNo(e.target.value)} /></div>
          <div><label className="sgp-label">E-way Bill No.</label>
            <input data-testid="sale-eway" className="sgp-input font-mono" value={ewayNo} onChange={(e) => setEwayNo(e.target.value)} /></div>
          <div><label className="sgp-label">LR No.</label>
            <input data-testid="sale-lr" className="sgp-input font-mono" value={lrNo} onChange={(e) => setLrNo(e.target.value)} /></div>
        </div>
      </div>

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr>
              <th className="sgp-th w-60">Item (from master)</th><th className="sgp-th">Batch (FEFO order)</th>
              <th className="sgp-th text-right">Qty</th><th className="sgp-th text-right">Rate</th>
              <th className="sgp-th text-right">Disc%</th><th className="sgp-th text-right">GST%</th>
              <th className="sgp-th text-right">Amount</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => {
              const m = items.find((x) => x.id === l.item_id);
              const base = (Number(l.qty) || 0) * (Number(l.rate) || 0);
              const amt = base - base * (Number(l.discount_pct) || 0) / 100;
              const bs = batchesFor(l.item_id, l.batch_id);
              return (
                <tr key={i} data-testid={`sale-line-${i}`}>
                  <td className="sgp-td">
                    {l.quoted_name && (
                      <p className="mb-1 text-[10px] font-semibold text-indigo-700">
                        Quoted as: {l.quoted_name}
                      </p>
                    )}
                    <select data-testid={`sale-item-select-${i}`} className="sgp-input" value={l.item_id}
                      onChange={(e) => {
                        const itemId = e.target.value;
                        const choices = batchesFor(itemId, "");
                        const fefo = choices.find((b) => Number(b.qty || 0) + 0.000001 >= Number(l.qty || 0)) || choices[0];
                        setLine(i, { item_id: itemId, batch_id: fefo?.id || "" });
                      }}>
                      <option value="">— Map to stock item —</option>
                      {items.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
                    </select>
                  </td>
                  <td className="sgp-td">
                    <select data-testid={`sale-batch-select-${i}`} className="sgp-input font-mono text-xs w-52" value={l.batch_id}
                      onChange={(e) => setLine(i, { batch_id: e.target.value })}>
                      <option value="">— Batch —</option>
                      {bs.map((b) => <option key={b.id} value={b.id}>{b.batch_no} | Exp {b.expiry} | Stk {b.qty}</option>)}
                    </select>
                    {l.item_id && !bs.length && <p className="text-[10px] text-rose-600 mt-1">No stock — pehle purchase karo</p>}
                    {l.batch_id && (() => {
                      const selected = batches.find((b) => b.id === l.batch_id);
                      const available = Number(selected?.qty || 0) + (editId && l.original_batch_id === l.batch_id ? Number(l.original_qty || 0) : 0);
                      return <p className={`text-[10px] mt-1 ${Number(l.qty) > available ? "text-rose-600 font-semibold" : "text-slate-500"}`}>Available: {available}{selected?.unit ? ` ${selected.unit}` : ""}</p>;
                    })()}
                  </td>
                  <td className="sgp-td"><input data-testid={`sale-qty-${i}`} type="number" step="0.001" min="0.001" className="sgp-input font-mono w-20 text-right" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`sale-rate-${i}`} type="number" step="0.01" min="0" className="sgp-input font-mono w-24 text-right" value={l.rate} onChange={(e) => setLine(i, { rate: e.target.value })} /></td>
                  <td className="sgp-td"><input data-testid={`sale-disc-${i}`} type="number" step="0.01" min="0" className="sgp-input font-mono w-16 text-right" value={l.discount_pct} onChange={(e) => setLine(i, { discount_pct: e.target.value })} /></td>
                  <td className="sgp-td text-right font-mono text-xs">{m ? `${m.gst_rate}%` : "-"}</td>
                  <td className="sgp-td text-right font-mono text-xs">{l.item_id ? fmt(amt) : "-"}</td>
                  <td className="sgp-td">
                    <button type="button" data-testid={`sale-line-del-${i}`} onClick={() => setLines(lines.length === 1 ? [{ ...emptyLine }] : lines.filter((_, j) => j !== i))}
                      className="p-1 rounded hover:bg-rose-50 text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="p-3 border-t border-slate-100">
          <button type="button" data-testid="sale-add-line" onClick={() => setLines([...lines, { ...emptyLine }])}
            className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md border border-dashed border-slate-300 text-slate-600 hover:border-teal-600 hover:text-teal-700">
            <Plus className="h-3.5 w-3.5" /> Add Item Row
          </button>
        </div>
      </div>

      <div className="sgp-card p-5 ml-auto max-w-sm space-y-1.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Taxable Amount</span><span data-testid="sale-taxable" className="font-mono">{fmt(totals.taxable)}</span></div>
        {!interstate && <>
          <div className="flex justify-between"><span className="text-slate-500">CGST</span><span data-testid="sale-cgst" className="font-mono">{fmt(totals.cgst)}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">SGST</span><span data-testid="sale-sgst" className="font-mono">{fmt(totals.sgst)}</span></div>
        </>}
        {interstate && <div className="flex justify-between"><span className="text-slate-500">IGST</span><span data-testid="sale-igst" className="font-mono">{fmt(totals.igst)}</span></div>}
        <div className="flex justify-between"><span className="text-slate-500">Round Off</span><span className="font-mono">{totals.roundOff.toFixed(2)}</span></div>
        <div className="flex justify-between border-t border-slate-200 pt-2 text-base font-bold">
          <span>Grand Total</span><span data-testid="sale-grand-total" className="font-mono text-teal-700">{fmt(totals.grand)}</span>
        </div>
      </div>
    </form>
  );
}

